import { TextInput, Icon, IconName, Alert, CircularProgress } from '@mgmresorts/mgm-ui';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { OktaUser } from '../../../@types/okta-user';
import { KnownError, UNKNOWN_ERROR_MESSAGE } from '../../api/errors';
import { requestChangePassword } from '../../api/fetch/change-password';
import { Confirmation } from '../../components/ui/Confirmation';
import { Container } from '../../components/ui/Container';
import { Logo } from '../../components/ui/Logo';
import { PageLayout } from '../../components/ui/PageLayout';
import { useContent } from '../../hooks/useContent';
import { useFieldValidation } from '../../hooks/useFieldValidation';
import { useInput } from '../../hooks/useInput';
import { usePasswordInput } from '../../hooks/usePasswordInput';
import { captureEvent } from '../../monitoring/logrocket';
import { useEvent } from '../../utils/analytics/useEvent';
import { usePageLoadEvent } from '../../utils/analytics/usePageLoadEvent';
import { useAuthContext } from '../../utils/auth/AuthProvider';
import { MaxLengths } from '../../utils/constants';
import { redirectToSignIn } from '../../utils/redirect';
import {
  StyledPasswordHintItem,
  StyledPasswordHintList,
  StyledPasswordHintText,
  StyledPasswordHint,
} from '../ResetPassword/styles';

import {
  StyledContent,
  StyledHeading,
  StyledAlertContainer,
  StyledPasswordContainer,
  StyledButton,
  StyledLink,
} from './styles';

export function ChangePassword() {
  usePageLoadEvent('sign in', 'change password');
  const trackEvent = useEvent();
  const [userInfo, setUserInfo] = useState<OktaUser | null>();
  const [error, setErrorState] = useState<null | string>(null);
  const [isLoading, setLoadingState] = useState(false);
  const { acquireToken, getUserInfo } = useAuthContext();
  const { validateField, invalidFields } = useFieldValidation();
  const [successfullyChanged, setSuccessfullyChanged] = useState(false);

  const { getContentByPath } = useContent();

  const defaultRedirect = `${window.location.origin}/account/profile`;

  const { bind: bindCurrentPassword, value: currentPassword } = useInput(
    '',
    'currentPassword',
    undefined,
    validateField,
  );

  const {
    bind: bindNewPassword,
    value: newPassword,
    isValid: isValidNewPassword,
    validations: passwordValidations,
  } = usePasswordInput('', 'newPassword', undefined, validateField);

  const { bind: bindReTypePassword, value: reTypePassword } = useInput('', 'reTypePassword', undefined, validateField);

  const resetPassword = useCallback(
    async e => {
      try {
        e.preventDefault();
        setLoadingState(true);

        captureEvent('Change Password', {
          email: userInfo?.profile?.login ?? 'undefined',
        });

        const token = await acquireToken();

        if (!token?.accessToken || token.isTransient) {
          setErrorState(UNKNOWN_ERROR_MESSAGE);
          return;
        }

        await requestChangePassword({
          oldPassword: currentPassword,
          newPassword,
        });

        setSuccessfullyChanged(true);
      } catch (error) {
        let message;
        if (error instanceof KnownError) {
          message = error.message;
        } else {
          message = UNKNOWN_ERROR_MESSAGE;
        }
        setErrorState(message);
      } finally {
        setLoadingState(false);
      }
    },
    [acquireToken, currentPassword, newPassword, userInfo?.profile?.login],
  );

  const passwordValidationHint = useCallback(
    (validation: keyof typeof passwordValidations, hint: string) => {
      const isValid = passwordValidations[validation];
      const className = !newPassword ? '' : isValid ? 'hint-success' : 'hint-error';
      const iconName: IconName = !newPassword || isValid ? 'checkmark-circle' : 'symbol-x-circle';

      const requirementText = getContentByPath(
        !newPassword
          ? 'shared.fields.requirement'
          : isValid
          ? 'shared.fields.requirementMet'
          : 'shared.fields.requirementUnmet',
      );
      if (!newPassword) {
        return (
          <StyledPasswordHintItem>
            <StyledPasswordHintText className={className} variant='body-regular-m' as='p'>
              {hint}
            </StyledPasswordHintText>
          </StyledPasswordHintItem>
        );
      }
      return (
        <StyledPasswordHint className={className} variant='body-regular-m' as='p'>
          <Icon
            name={iconName}
            size='small'
            role='img'
            aria-hidden={false}
            focusable
            aria-label={requirementText as string}
          />
          {hint}
        </StyledPasswordHint>
      );
    },
    [getContentByPath, newPassword, passwordValidations],
  );

  const currentPasswordError = useMemo(
    () =>
      invalidFields.includes('currentPassword')
        ? getContentByPath<string>('changePassword.fields.currentPassword.error')
        : '',
    [invalidFields, getContentByPath],
  );

  const newPasswordError = useMemo(() => {
    if (invalidFields.includes('newPassword')) {
      return getContentByPath<string>('changePassword.fields.newPassword.error');
    }
    if (newPassword && currentPassword && newPassword === currentPassword) {
      return getContentByPath<string>('changePassword.fields.newPassword.errorNotEqual');
    }
    return '';
  }, [currentPassword, getContentByPath, invalidFields, newPassword]);

  const reTypePasswordError = useMemo(() => {
    if (invalidFields.includes('reTypePassword')) {
      return getContentByPath<string>('changePassword.fields.reTypePassword.error');
    }
    if (reTypePassword && newPassword && reTypePassword !== newPassword) {
      return getContentByPath<string>('changePassword.fields.reTypePassword.errorNotEqual');
    }
    return '';
  }, [getContentByPath, invalidFields, newPassword, reTypePassword]);

  const allRequirementsMet = useMemo(
    () =>
      currentPassword &&
      newPassword &&
      reTypePassword &&
      isValidNewPassword &&
      !currentPasswordError &&
      !reTypePasswordError &&
      !newPasswordError,
    [
      currentPassword,
      currentPasswordError,
      isValidNewPassword,
      newPassword,
      newPasswordError,
      reTypePassword,
      reTypePasswordError,
    ],
  );

  useEffect(() => {
    const errors = {
      'Old Password': currentPasswordError,
      'New Password': newPasswordError,
      'Re-Type Password': reTypePasswordError,
    };
    if (currentPasswordError || newPasswordError || reTypePasswordError) {
      trackEvent(
        'identity – update password - credentials input error',
        'update password credentials input error',
        'updatepinputerror',
        {
          errorField: Object.entries(errors)
            .filter(([, message]) => message)
            .map(([error]) => error)
            .join(','),
          errorDisplayed: Object.entries(errors)
            .filter(([, message]) => !!message)
            .map(([, message]) => message)
            .join(','),
        },
      );
    }
  }, [currentPasswordError, newPasswordError, reTypePasswordError, trackEvent]);

  useEffect(() => {
    if (error) {
      trackEvent(
        'identity – update password - credentials submit error',
        'update password credentials submit error',
        'updatepasswordsubmitfail',
        {
          updatePasswordState: 'failed',
          errorDisplayed: error,
        },
      );
    }
  }, [error, trackEvent]);

  useEffect(() => {
    if (successfullyChanged) {
      trackEvent(
        'identity – update password - credentials submit success',
        'update password credentials submit success',
        'updatepsubmitsuccess',
        {
          updatePasswordState: 'success',
        },
      );
    }
  }, [successfullyChanged, trackEvent]);

  useEffect(() => {
    getUserInfo().then(setUserInfo);
  }, [getUserInfo]);

  useEffect(() => {
    if (userInfo) {
      trackEvent('identity – update password - start', 'update password start', 'updatepasswordstart', {}, true);
    }
  }, [trackEvent, userInfo]);

  if (userInfo === undefined) {
    return (
      <PageLayout
        testId='route-/change-password'
        title={getContentByPath('changePassword.title')}
        description={getContentByPath('changePassword.description')}
      >
        <div data-testid='page-title' />
        <Container height='692px'>
          <Centered>
            <CircularProgress data-testid='change-password-loading' size={48} indeterminate />
          </Centered>
        </Container>
      </PageLayout>
    );
  }

  if (successfullyChanged) {
    return (
      <Confirmation
        title={getContentByPath('changePassword.successfullyChanged.title')}
        description={getContentByPath<string>('changePassword.successfullyChanged.content')}
        redirectCTA={{
          label: getContentByPath('changePassword.successfullyChanged.button'),
          action: async () => {
            await trackEvent(
              'identity – update password - back to account',
              'back to account',
              'updatepbacktoaccount',
              {},
              true,
            );
            window.location.href = defaultRedirect;
          },
        }}
      />
    );
  }

  if (userInfo === null) {
    redirectToSignIn();
  }

  const renderPasswordHints = () => (
    <>
      {passwordValidationHint('length', getContentByPath('changePassword.fields.newPassword.validation.lengthHint'))}
      {passwordValidationHint('number', getContentByPath('changePassword.fields.newPassword.validation.numberHint'))}
      {passwordValidationHint(
        'uppercase',
        getContentByPath('changePassword.fields.newPassword.validation.uppercaseHint'),
      )}
      {passwordValidationHint(
        'lowercase',
        getContentByPath('changePassword.fields.newPassword.validation.lowercaseHint'),
      )}
      {passwordValidationHint(
        'specialCharacter',
        getContentByPath('changePassword.fields.newPassword.validation.specialCharacterHint'),
      )}
    </>
  );

  return (
    <StyledContent>
      <PageLayout
        title={getContentByPath('changePassword.title')}
        description={getContentByPath('changePassword.description')}
      >
        <Container height='auto'>
          <Logo />
          <StyledHeading variant='heading-medium-3' as='h1' data-testid='page-title'>
            {getContentByPath('changePassword.title')}
          </StyledHeading>

          {error && (
            <StyledAlertContainer>
              <Alert open hiddenCloseButton variant='error' message={error} data-testid='change-password-error' />
            </StyledAlertContainer>
          )}
          {!isValidNewPassword && (
            <div data-testid='hints-wrapper'>
              {!newPassword ? (
                <StyledPasswordHintList>{renderPasswordHints()}</StyledPasswordHintList>
              ) : (
                <>{renderPasswordHints()}</>
              )}
            </div>
          )}
          {allRequirementsMet && (
            <StyledPasswordHint variant='body-regular-s' className='hint-success' testId='reset-password-all-met'>
              <Icon
                name='checkmark-circle'
                size='small'
                role='img'
                aria-hidden='false'
                focusable
                aria-label={getContentByPath('changePassword.fields.newPassword.validation.allMet')}
              />
              {getContentByPath('changePassword.fields.newPassword.validation.allMet')}
            </StyledPasswordHint>
          )}
          <div>
            <StyledPasswordContainer>
              <TextInput
                variant='password'
                label={getContentByPath('changePassword.fields.currentPassword.title')}
                id='currentPassword'
                disabled={isLoading}
                error={!!currentPasswordError}
                hint={currentPasswordError}
                {...bindCurrentPassword}
                maxLength={MaxLengths.Password}
                autoComplete='current-password'
              />
            </StyledPasswordContainer>
            <StyledPasswordContainer>
              <TextInput
                variant='password'
                label={getContentByPath('changePassword.fields.newPassword.title')}
                id='newPassword'
                disabled={isLoading}
                error={!!newPasswordError}
                hint={newPasswordError}
                {...bindNewPassword}
                maxLength={MaxLengths.Password}
                autoComplete='new-password'
              />
            </StyledPasswordContainer>
            <StyledPasswordContainer>
              <TextInput
                variant='password'
                label={getContentByPath('changePassword.fields.reTypePassword.title')}
                id='reTypePassword'
                disabled={isLoading}
                error={!!reTypePasswordError}
                hint={reTypePasswordError}
                {...bindReTypePassword}
                maxLength={MaxLengths.Password}
                autoComplete='new-password'
              />
            </StyledPasswordContainer>
            <StyledButton
              label={getContentByPath('changePassword.changePassword')}
              variant='primary'
              size='large'
              onClick={resetPassword}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  resetPassword(e);
                }
              }}
              disabled={!allRequirementsMet || isLoading}
              testId='change-password'
              type='submit'
              loading={isLoading}
              fullWidth
            />
            <StyledLink
              label={getContentByPath('changePassword.backToAccount')}
              size='large'
              variant='tertiary'
              icon='arrow-left'
              fullWidth
              onClick={async () => {
                await trackEvent(
                  'identity – update password - back to account',
                  'back to account',
                  'updatepbacktoaccount',
                  {},
                  true,
                );
                window.location.href = defaultRedirect;
              }}
              onKeyDown={async e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  await trackEvent(
                    'identity – update password - back to account',
                    'back to account',
                    'updatepbacktoaccount',
                    {},
                    true,
                  );
                  window.location.href = defaultRedirect;
                }
              }}
            />
          </div>
        </Container>
      </PageLayout>
    </StyledContent>
  );
}

ChangePassword.displayName = 'ChangePassword';

export const Centered = styled.div`
  margin: auto;
`;
